import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Star, ChevronUp, ChevronDown } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { SponsorType } from "@shared/schema";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function ManageSponsorTypesDialog({ open, onOpenChange, inPanel = false }: Props) {
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const { data: types = [], isLoading } = useQuery<SponsorType[]>({
    queryKey: ["/api/sponsor-hub/types"],
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => apiRequest("POST", "/api/sponsor-hub/types", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/types"] });
      setNewName("");
      toast({ title: "Sponsor type created" });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Could not create type", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      apiRequest("PATCH", `/api/sponsor-hub/types/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/types"] });
      setEditingId(null);
      toast({ title: "Sponsor type updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Could not update type", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/sponsor-hub/types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/types"] });
      toast({ title: "Sponsor type deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Could not delete type", variant: "destructive" }),
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/sponsor-hub/types/${id}/set-default`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/types"] });
      toast({ title: "Default updated" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) =>
      apiRequest("POST", "/api/sponsor-hub/types/reorder", { orderedIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/types"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Could not reorder", variant: "destructive" }),
  });

  const moveType = (idx: number, direction: "up" | "down") => {
    const newOrder = [...types];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    reorderMutation.mutate(newOrder.map((t) => t.id));
  };

  const content = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="new-sponsor-type">Add sponsor type</Label>
        <div className="flex gap-2">
          <Input
            id="new-sponsor-type"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Corporate, Local Business, Non-profit"
            data-testid="input-new-sponsor-type"
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) createMutation.mutate(newName.trim());
            }}
          />
          <Button
            onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
            disabled={createMutation.isPending || !newName.trim()}
            data-testid="button-add-sponsor-type"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : types.length === 0 ? (
        <p className="text-sm text-muted-foreground">No sponsor types yet. Add one above.</p>
      ) : (
        <div className="space-y-2">
          {types.map((t, idx) => (
            <Card key={t.id} className="shadow-none border">
              <CardContent className="p-3 flex items-center gap-2">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30"
                    title="Move up"
                    onClick={() => moveType(idx, "up")}
                    disabled={idx === 0 || reorderMutation.isPending}
                    data-testid={`button-move-up-sponsor-type-${t.id}`}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30"
                    title="Move down"
                    onClick={() => moveType(idx, "down")}
                    disabled={idx === types.length - 1 || reorderMutation.isPending}
                    data-testid={`button-move-down-sponsor-type-${t.id}`}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>

                {editingId === t.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="h-8 flex-1"
                      autoFocus
                      data-testid={`input-edit-sponsor-type-${t.id}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") updateMutation.mutate({ id: t.id, name: editingName.trim() });
                        if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                    <Button size="sm" onClick={() => updateMutation.mutate({ id: t.id, name: editingName.trim() })} disabled={updateMutation.isPending} data-testid={`button-save-sponsor-type-${t.id}`}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-testid={`button-cancel-sponsor-type-${t.id}`}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium" data-testid={`text-sponsor-type-name-${t.id}`}>{t.name}</span>
                    {t.isDefault && <Badge variant="secondary" className="text-xs">Default</Badge>}
                    <button
                      className="p-1 rounded hover:bg-muted text-muted-foreground"
                      title="Set as default"
                      onClick={() => setDefaultMutation.mutate(t.id)}
                      data-testid={`button-default-sponsor-type-${t.id}`}
                    >
                      <Star className={`w-3.5 h-3.5 ${t.isDefault ? "fill-yellow-400 text-yellow-400" : ""}`} />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-muted text-muted-foreground"
                      onClick={() => { setEditingId(t.id); setEditingName(t.name); }}
                      data-testid={`button-edit-sponsor-type-${t.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-muted text-destructive"
                      onClick={() => deleteMutation.mutate(t.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`button-delete-sponsor-type-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (inPanel) return content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Sponsor Types</DialogTitle>
          <DialogDescription>
            Define the sponsor categories used across Sponsor Hub leads and clients. Drag to reorder.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
